export interface EventHandler {
  (data: unknown): void;
}

export interface EventUnsubscribe {
  (): void;
}

export interface IEventEmitter {
  emit(event: string, payload: unknown): void;
  on(event: string, callback: EventHandler): EventUnsubscribe;
  once(event: string, callback: EventHandler): EventUnsubscribe;
  off(event: string, callback: EventHandler): void;
  removeAllListeners(): void;
}

export interface IInventoryEventEmitter extends IEventEmitter {
  emitStockChanged(stockId: string, quantity: number): void;
  emitLowStock(productId: string, quantity: number, threshold: number): void;
  emitStockOut(productId: string): void;
  onStockChanged(callback: (data: { stockId: string; quantity: number }) => void): EventUnsubscribe;
  onLowStock(callback: (data: { productId: string; quantity: number; threshold: number }) => void): EventUnsubscribe;
  onStockOut(callback: (data: { productId: string }) => void): EventUnsubscribe;
}

export class EventEmitter implements IInventoryEventEmitter {
  private handlers: Map<string, Set<EventHandler>> = new Map();

  public emit(event: string, payload: unknown): void {
    const handlers = this.handlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(payload);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  public on(event: string, callback: EventHandler): EventUnsubscribe {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    
    const handlers = this.handlers.get(event)!;
    handlers.add(callback);

    return () => {
      this.off(event, callback);
    };
  }

  public once(event: string, callback: EventHandler): EventUnsubscribe {
    const onceCallback: EventHandler = (data: unknown) => {
      callback(data);
      this.off(event, onceCallback);
    };

    return this.on(event, onceCallback);
  }

  public off(event: string, callback: EventHandler): void {
    const handlers = this.handlers.get(event);
    if (handlers) {
      handlers.delete(callback);
      if (handlers.size === 0) {
        this.handlers.delete(event);
      }
    }
  }

  public removeAllListeners(): void {
    this.handlers.clear();
  }

  // Inventory-specific events
  public emitStockChanged(stockId: string, quantity: number): void {
    this.emit('stockChanged', { stockId, quantity });
  }

  public emitLowStock(productId: string, quantity: number, threshold: number): void {
    this.emit('lowStock', { productId, quantity, threshold });
  }

  public emitStockOut(productId: string): void {
    this.emit('stockOut', { productId });
  }

  public onStockChanged(callback: (data: { stockId: string; quantity: number }) => void): EventUnsubscribe {
    return this.on('stockChanged', callback as EventHandler);
  }

  public onLowStock(callback: (data: { productId: string; quantity: number; threshold: number }) => void): EventUnsubscribe {
    return this.on('lowStock', callback as EventHandler);
  }

  public onStockOut(callback: (data: { productId: string }) => void): EventUnsubscribe {
    return this.on('stockOut', callback as EventHandler);
  }
}